import { useCallback } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import {
  ListadoActualizando, ListadoCargando, ListadoConError,
} from '../../../nucleo/listados/EstadosDeListado.jsx';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../../../nucleo/listados/useConsultaDeListado.js';
import { consultarPanel } from '../api/vencimientosApi.js';
import { RenglonDeVencimiento } from '../componentes/RenglonDeVencimiento.jsx';
import { importe as formatearEnDivisa } from '../formato.js';

/**
 * PESTAÑA «VENCIMIENTOS» DEL DESTINO CUENTAS (RF-120, RF-122, D-185).
 *
 * El 6/6 NO registra sección de navegación y el shell no se toca: la barra sigue
 * con sus cinco lugares de módulo y el sexto de Ajustes (RN-044b, RN-044c).
 *
 * MANDA `incluirSinSaldo = true` y muestra SIEMPRE las tarjetas sin saldo, con sus
 * fechas y su corte en curso (D-192, RN-290). NO SE DIBUJA CASILLA, INTERRUPTOR,
 * CHIP NI FILTRO para ocultarlas: nadie lo pidió, y esconder la tarjeta que no debe
 * nada es esconder precisamente «cuándo me corta y cuánto llevo».
 *
 * NO REORDENA (RN-273, RN-292): la lista llega ordenada por el servidor —vencidas
 * de más atrasada a menos, luego las que avisan, luego el resto— y se pinta tal
 * cual. Si esta pantalla aplicara un `sort` propio, sería defecto aunque el
 * resultado coincidiera el día que se probó.
 */
export default function PanelDeVencimientos({ base = '/cuentas/vencimientos' }) {
  const hoy = obtenerFechaDeNegocioHoy();

  const consultar = useCallback(
    // `fechaLocal` es la del dispositivo; el servidor no la suple (D-06, RNF-057).
    () => consultarPanel({ fechaLocal: hoy, incluirSinSaldo: true }),
    [hoy],
  );

  const listado = useConsultaDeListado(
    consultar,
    (respuesta) => (respuesta?.tarjetas ?? []).length === 0,
  );

  const panel = listado.datos;
  const totales = panel?.totalesPorMoneda ?? [];
  const sinPendientes = totales.length > 0 && totales.every((t) => Number(t.totalPorPagar) === 0);

  return (
    <Shell titulo="Cuentas" subtitulo="Vencimientos de tarjetas">
      <ListadoActualizando visible={listado.actualizando} />

      <nav className="pestanas" aria-label="Pestañas de Cuentas">
        <Link to="/cuentas">Cuentas</Link>
        <Link to="/cuentas/tarjetas">Tarjetas</Link>
        <Link to="/cuentas/meses">Meses</Link>
        <Link className="on" aria-current="page" to={base}>Vencimientos</Link>
      </nav>

      {listado.estado === ESTADO_DE_LISTADO.ERROR && (
        <ListadoConError error={listado.error} alReintentar={listado.recargar} />
      )}

      {listado.estado === ESTADO_DE_LISTADO.CARGANDO && (
        <ListadoCargando texto="Consultando tus vencimientos…" />
      )}

      {/* SIN TARJETAS NO ES ERROR: una lista vacía es un resultado (caso borde 1). */}
      {panel && listado.estado === ESTADO_DE_LISTADO.VACIO && (
        <div className="card" data-prueba="panel-vacio">
          <b>Todavía no tienes tarjetas de crédito registradas</b>
          <span className="ayuda">
            Cuando des de alta una tarjeta con su día de corte y su fecha límite, aparecerá aquí con
            cuánto hay que pagar y cuándo.
          </span>
          <Link className="btn ancho" to="/cuentas/nueva">Registrar una tarjeta</Link>
        </div>
      )}

      {panel && listado.estado === ESTADO_DE_LISTADO.CON_DATOS && (
        <>
          {/* TOTALES POR MONEDA: solo lo pendiente de las vencidas y las por vencer.
              MXN y USD NUNCA se combinan (RN-003, RN-275). El corte en curso NO
              suma aquí y las mensualidades TAMPOCO (RN-283, RN-268). */}
          <div className="card" data-prueba="totales-por-moneda">
            <h3>Total por pagar</h3>
            {sinPendientes && (
              <div className="aviso ok" role="status" data-prueba="sin-pendientes">
                No tienes pagos pendientes. El panel no se oculta: sigue mostrando las fechas de corte
                y el ciclo en curso de cada tarjeta.
              </div>
            )}
            {totales.map((total) => (
              <div className="fila" key={total.codigoISO} data-prueba={`total-${total.codigoISO}`}>
                <span className="crece">
                  {total.codigoISO} · {total.cantidadVencidas}
                  {total.cantidadVencidas === 1 ? ' vencida' : ' vencidas'} y {total.cantidadPorVencer} por vencer
                </span>
                <b>{formatearEnDivisa(total.totalPorPagar, total.codigoISO)}</b>
              </div>
            ))}
            {totales.length > 1 && (
              <span className="ayuda">
                Cada moneda va por su lado y nunca se combinan: no hay tipo de cambio en el sistema y
                el importe en dólares se muestra sin convertir.
              </span>
            )}
            {panel.hayTarjetasSinEstado && (
              <div className="aviso e" role="status" data-prueba="total-incompleto">
                {/* Fila 19 del inventario del incremento 15 (RF-222/RF-223): salió la jerga
                    técnica («viajó sin estado») y la justificación del diseño. */}
                ⚠ Este total está incompleto: no se pudo consultar el saldo de una de tus tarjetas y
                no está sumada aquí.
              </div>
            )}
            <span className="ayuda">
              Este total solo lleva lo pendiente de las tarjetas vencidas y por vencer. Lo que llevas
              acumulado en el ciclo que aún no corta no entra, y las mensualidades a meses tampoco.
            </span>
          </div>

          {/* Pie con los días aplicados y su enlace a Configuración (contratos §1). */}
          <div className="card" data-prueba="pie-de-configuracion">
            <div className="fila">
              <span className="crece">
                Aviso con <b data-prueba="dias-aplicados">{panel.diasDeAnticipacionAplicados}</b>
                {panel.diasDeAnticipacionAplicados === 1 ? ' día' : ' días'} de anticipación
                {panel.diasDeAnticipacionAplicados === 0 && ' · solo el mismo día del vencimiento'}
                <small>
                  Es tu ajuste personal, no el de la casa. Cambiarlo mueve el resalte, nunca la lista
                  ni los importes.
                </small>
              </span>
              <Link className="btn sec" to={`${base}/configuracion`}>Cambiar</Link>
            </div>
          </div>

          {/* [NUEVO · incremento 19/21 · RF-259] Entrada a la comparación entre lo
              que Deisy Paez declaró a mano y lo que respaldan sus planes. Se pone
              aquí, en el panel, porque es donde se ve la cifra que el traslape
              distorsiona: lo que se espera pagar. */}
          <div className="card" data-prueba="pie-de-comparacion">
            <div className="fila">
              <span className="crece">
                Tus compras a meses declaradas a mano
                <small>
                  Compáralas con los planes que ya registraste. Abrir la comparación no cambia
                  ninguna cifra.
                </small>
              </span>
              <Link className="btn sec" to={`${base}/comparacion`} data-prueba="ir-a-comparacion">
                Comparar
              </Link>
            </div>
          </div>

          {panel.tarjetas.map((tarjeta) => (
            <RenglonDeVencimiento
              key={tarjeta.idCuenta}
              tarjeta={tarjeta}
              rutaDeDetalle={`${base}/${tarjeta.idCuenta}`}
            />
          ))}
        </>
      )}
    </Shell>
  );
}
